import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  type ConversionSnapshot,
  type ItemsFormState,
  type LoadedConversion,
  SPLIT_CHANGE_KEYS,
  type ScanFormSnapshot,
  type ScanMerge,
  cadenceSentence,
  deriveFrozenRate,
  dropItemsPayload,
  hydrateItemsState,
  isSameCurrency,
  itemsAvailableIn,
  itemsListEdit,
  offlineExpenseService,
  orderPickerMembers,
  previewLineItemSplit,
  queryKeys,
  randomUuid,
  receiptDraftDropped,
  recurringErrorKey,
  roundLineItems,
  seedItemsFromScan,
  stripParticipantPicks,
  toItemsPayload,
  unpickedSplitForEdit,
  unpickedSplitFromGroup,
  useCategoryPredictionScheduler,
  useExpenseConversion,
  useFormWithValidation,
  useRepeatChoice,
} from '@splitfiesta/core';
import {
  type ExpenseFormData,
  type ExtendedGroup,
  type Payer,
  type Split,
  expenseFormService,
} from '@splitfiesta/core/services/expense/ExpenseFormService';
import { splitCalculationService } from '@splitfiesta/core/services/expense/SplitCalculationService';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type CategoryPredictionSource,
  type ExpenseLineItem,
  GROUP_EXPENSE_LIMIT_ERROR_KEYS,
  type GroupProDto,
  LINE_ITEMS_ERROR_KEYS,
  type LineItemsRest,
  type ReceiptScanLineItemDto,
  SplitMethod,
  type UnpickedSplit,
  type UserProDto,
  oddCentRecipient,
  pickersOf,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

import { useExchangeRateQuery } from '@/hooks/queries/useExchangeRateQuery';
import {
  useCreateExpenseMutation,
  useDeleteExpenseReceiptMutation,
  useExpenseQuery,
  useUpdateExpenseMutation,
  useUploadExpenseReceiptMutation,
} from '@/hooks/queries/useExpenses';
import { useGroupQuery } from '@/hooks/queries/useGroups';
import {
  type CloudPredictionResult,
  type LivePrediction,
  useCategoryPrediction,
} from '@/hooks/useCategoryPrediction';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useRouter } from '@/hooks/useRouteChangeRouter';

import {
  MIN_CATEGORY_CONFIDENCE,
  MIN_SUBCATEGORY_CONFIDENCE,
  MIN_TITLE_CHARS,
} from '@/services/categoryPrediction/ExpenseCategoryService';

import { useAuthStore } from '@/stores/authStore';

import { celebrate } from '@/utils/confetti';
import { toast } from '@/utils/toast';

/** Which picker saved the spot. */
export type SavedSpotRole = 'payer' | 'sharer';
export interface SavedSpotMember {
  userId: string;
  name: string;
}

interface UseExpenseFormProps {
  groupId: string;
  expenseId?: string;
  mode?: 'create' | 'edit';
  /** The `pro` feature flag; split by item needs it. */
  proFlagOn?: boolean;
}

const EMPTY_ITEMS: ItemsFormState = {
  itemsMode: false,
  lineItems: [],
  rest: { mode: 'proportional' },
  participants: [],
  fromScan: false,
  scanReconciled: false,
  scanUnreconciled: false,
};

export function useExpenseForm({
  groupId,
  expenseId,
  mode = 'create',
  proFlagOn = false,
}: UseExpenseFormProps) {
  const user = useAuthStore(state => state.user);
  const queryClient = useQueryClient();
  const { t: tExpenses } = useTranslation('expenses');
  const { t } = useTranslation();
  const router = useRouter();

  // Use React Query hooks
  const { data: groupData, isLoading: groupLoading } = useGroupQuery(
    groupId,
    !!user
  );
  // The persisted cache may be days old; the form re-hydrates from the
  // refetch while the person has not edited anything.
  const { data: expenseData, isLoading: expenseLoading } = useExpenseQuery(
    expenseId || '',
    mode === 'edit' && !!expenseId,
    { refetchOnMount: 'always' }
  );
  const createExpenseMutation = useCreateExpenseMutation();
  const updateExpenseMutation = useUpdateExpenseMutation(expenseId || '');
  const uploadReceiptMutation = useUploadExpenseReceiptMutation();
  const deleteReceiptMutation = useDeleteExpenseReceiptMutation();
  const { isOnline } = useNetworkStatus();

  // Form state
  const [group, setGroup] = useState<ExtendedGroup | null>(null);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState<string | undefined>('');
  // What the user typed, in `enteredCurrency` — the source of truth for
  // the amount. `numericAmount` (below) is derived from this via
  // `conversion`, never stored directly.
  const [enteredAmount, setEnteredAmount] = useState<number>(0);
  const [enteredCurrencyState, setEnteredCurrencyState] = useState('');
  // Written with every currency change: a scan fills the currency and seeds
  // its lines in one tick, before a render could carry the new state.
  const enteredCurrencyRef = useRef(enteredCurrencyState);
  // User-supplied override rate; wins over any preview/frozen rate.
  const [manualRate, setManualRateState] = useState<number | null>(null);
  // Edit mode: the loaded expense's currency, date, stored rate and
  // amounts — the baseline `frozen` is derived from below.
  const [loadedConversion, setLoadedConversion] =
    useState<LoadedConversion | null>(null);
  // The loaded expense's own conversion fields, forwarded to
  // `createUpdateExpenseDto` so it can clear a manual rate that's no
  // longer in use.
  const [existingConversion, setExistingConversion] = useState<{
    originalCurrency?: string;
    rateSource?: 'provider' | 'manual';
    exchangeRate?: number;
  } | null>(null);
  const [category, setCategory] = useState('other');
  const [subcategory, setSubcategory] = useState<string | undefined>(undefined);
  const [date, setDateState] = useState(
    expenseFormService.getLocalDateString()
  );
  const [paidBy, setPaidBy] = useState<Payer[]>([]);
  const [splitMethod, setSplitMethod] = useState<SplitMethod>('equal');
  const [splits, setSplits] = useState<Split[]>([]);
  // Split by item. `participants` is derived from the include state in
  // `splits`, which every split method shares.
  const [items, setItems] = useState<ItemsFormState | null>(null);
  const itemsRef = useRef(items);
  // Read by the create form's seeding effect, which must not re-seed the
  // method or the include list while items mode is on.
  const seededGroupIdRef = useRef<string | null>(null);
  // Any split change by the person; a scan then never switches to items.
  const splitTouchedRef = useRef(false);
  // From the first spot saved from a picker (success or not) until the group
  // re-seeds, a member-list change updates the split as it stands (newcomers
  // left out, leavers dropped): the create form would otherwise re-seed it on
  // every refetch.
  const keepSplitOnMemberChangeRef = useRef(false);
  // Spots placed by this form; a read from before the create must not drop them.
  const placedSpotIdsRef = useRef<Set<string>>(new Set());
  // The pickers' order: unlike the set above it outlives the read that lists
  // a spot, and only a re-seed clears it.
  const [newSpotIds, setNewSpotIds] = useState<readonly string[]>([]);
  // Captured at hydration: once the person has edited, a member's claim
  // arriving later must still fail the conflict check.
  const expectedUpdatedAtRef = useRef<string | undefined>(undefined);
  // Any change by the person since hydration; until then a fresher row
  // re-hydrates the form.
  const dirtyRef = useRef(false);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);
  const loadedHadItemsRef = useRef(false);
  // The weights the loaded itemized row was saved with, and over whom.
  const [storedUnpicked, setStoredUnpicked] = useState<{
    split?: UnpickedSplit;
    participants: string[];
  } | null>(null);
  const [pendingScanLines, setPendingScanLines] = useState<{
    lines: ReceiptScanLineItemDto[];
    reconciled: boolean;
  } | null>(null);
  const [pendingGroupSwitch, setPendingGroupSwitch] =
    useState<SplitMethod | null>(null);
  const [notes, setNotes] = useState('');
  const [categoryManuallyChanged, setCategoryManuallyChanged] = useState(false);
  // A receipt scan's category: a manual pick for the predictor gate and the
  // offline upgrade, but kept when the title is shorter than the floor.
  // Only a chip tap or a new scan replaces it.
  const [categoryFromScan, setCategoryFromScan] = useState(false);
  // Holds the predictor from the moment a scan starts, dropping its answers.
  const [scanInFlight, setScanInFlight] = useState(false);
  // The receipt block is past its base state (a photo encoding or uploading,
  // before any draft id exists): a re-hydration would remount it mid-upload.
  const [receiptBusy, setReceiptBusy] = useState(false);
  // Which fields the person has given a value; a scan only fills the others.
  const touchedRef = useRef({ amount: false, title: false, date: false });
  // Sticky "a prediction has been applied" (or the expense loaded in edit
  // mode). Together with the manual-pick flag and the title floor it
  // derives the returned `categoryPredicted`, which gates the category
  // UI and Save — so it must NOT reset on every keystroke the way the
  // scheduler's per-title `isSettled` does.
  const [categoryPredicted, setCategoryPredicted] = useState(false);
  // Bumped on title blur: the user is done with the title, so the
  // scheduler runs both tiers now instead of waiting out its timers.
  const [flushNonce, setFlushNonce] = useState(0);
  // Latest title, read inside `onPrediction` (which must stay identity-
  // stable) to key the held-back placeholder to the title it is for.
  const titleRef = useRef('');
  // The free tier's `other` placeholder for the current title, held back
  // while the paid tier is still expected to answer. Shown only if the
  // cloud declines — a chip that later changes reads as a glitch, and
  // "Other" is not a decision the app made.
  const [deferredFallback, setDeferredFallback] = useState<{
    title: string;
    source: CategoryPredictionSource;
    confidence: number;
  } | null>(null);
  // Once the fallback has been shown, a paid-tier retry (which re-arms
  // `isCloudPending`) must not hide the category section again.
  const [fallbackShown, setFallbackShown] = useState(false);
  /**
   * Metadata about the last auto-applied prediction. Cleared whenever
   * the user manually picks a category. Forwarded to the create
   * mutation so the offline queue (`PendingExpense`) carries the
   * source + confidence, which is what the sync-time cloud upgrade
   * keys off of (see runOfflineSync → maybeUpgradeCategoryWithCloud).
   */
  const [predictionMeta, setPredictionMeta] = useState<{
    source: CategoryPredictionSource;
    confidence: number;
  } | null>(null);
  const [payersManuallyChanged, setPayersManuallyChanged] = useState(false);

  // Receipt state
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState<string | null>(
    null
  );
  const [receiptDeleted, setReceiptDeleted] = useState(false);
  // Promoted into the receipt by the server on save; replaces the
  // post-save upload and delete.
  const [receiptDraftId, setReceiptDraftId] = useState<string | null>(null);

  // Edit mode hydrates once per expense id, and again from a fresher row
  // only while nothing was edited, so a refetch cannot wipe unsaved edits.
  const hydratedIdRef = useRef<string | null>(null);
  const [hydratedExpenseId, setHydratedExpenseId] = useState<string | null>(
    null
  );
  const [rehydrateNonce, setRehydrateNonce] = useState(0);
  // Changes on every hydration, so the scan machine reads the saved receipt again.
  const [hydrationKey, setHydrationKey] = useState<string | null>(null);

  const { handleError } = useErrorHandler('expenses');

  // The group's currency is fixed — no local state, just derived from
  // the loaded group so it can never drift from it.
  const groupCurrency = group?.currency ?? '';
  const isForeign = !isSameCurrency(enteredCurrencyState, groupCurrency);
  const frozen = deriveFrozenRate(loadedConversion, {
    enteredCurrency: enteredCurrencyState,
    date,
    manualRate,
  });

  // Changing the entered currency keeps the typed amount — re-rounded to
  // the new currency's precision — but drops a manual override, which
  // belongs to the old pair (`frozen` follows on its own). `enteredAmount` is a
  // dep (not `[]`) so the re-round always reads the current value, never
  // a stale one from when this callback was created.
  const setEnteredCurrency = useCallback(
    (value: string) => {
      markDirty();
      setEnteredCurrencyState(value);
      enteredCurrencyRef.current = value;
      setItems(prev =>
        prev
          ? { ...prev, lineItems: roundLineItems(prev.lineItems, value) }
          : prev
      );
      const rounded = roundToCurrencyPrecision(enteredAmount, value);
      setEnteredAmount(rounded);
      // Only touch the displayed string when rounding actually changed
      // the value — otherwise keep the user's exact typed string (e.g.
      // "50" stays "50" rather than becoming "50.00").
      if (rounded !== enteredAmount) {
        setAmount(expenseFormService.formatAmountForCurrency(rounded, value));
      }
      setManualRateState(null);
    },
    [enteredAmount, markDirty]
  );

  // Kept under its original name so existing consumers (ExpenseForm.tsx
  // → ExpenseBasicInfo) keep working unchanged. Internally it now
  // updates `enteredAmount` — the group-currency `numericAmount` the
  // hook returns is always derived from it via `conversion` below.
  // The stored rate survives an amount edit: the server keeps the frozen
  // rate for an amount-only change and checks the invariant against it,
  // so re-fetching the preview here would compute the total on whatever
  // table serves the date today and fail the save the day after the
  // expense was entered. Only a date or currency change unfreezes (see
  // `deriveFrozenRate`).
  const setNumericAmount = useCallback(
    (value: number) => {
      markDirty();
      touchedRef.current.amount = true;
      setEnteredAmount(value);
    },
    [markDirty]
  );

  /** The Split by item dialog's "Set total": the typed amount becomes the items' sum. */
  const setAmountFromItems = useCallback(
    (value: number) => {
      const entered = enteredCurrencyRef.current;
      const rounded = roundToCurrencyPrecision(value, entered);
      markDirty();
      touchedRef.current.amount = true;
      setEnteredAmount(rounded);
      setAmount(expenseFormService.formatAmountForCurrency(rounded, entered));
    },
    [markDirty]
  );

  const setTitleByUser = useCallback(
    (value: string) => {
      markDirty();
      touchedRef.current.title = true;
      setTitle(value);
    },
    [markDirty]
  );

  // The server keeps a stored manual rate across a date move, so carry it
  // into `manualRate` rather than letting the preview replace it.
  const applyDate = useCallback(
    (value: string) => {
      setDateState(value);
      if (frozen?.source === 'manual') setManualRateState(frozen.rate);
    },
    [frozen]
  );

  const setDate = useCallback(
    (value: string) => {
      markDirty();
      touchedRef.current.date = true;
      applyDate(value);
    },
    [applyDate, markDirty]
  );

  const setManualRate = useCallback(
    (rate: number) => {
      markDirty();
      setManualRateState(rate);
    },
    [markDirty]
  );

  const clearManualRate = useCallback(() => {
    setManualRateState(null);
  }, []);

  const rateQuery = useExchangeRateQuery({
    from: enteredCurrencyState,
    to: groupCurrency,
    date,
    enabled: isOnline && !frozen && manualRate === null && !!date && isForeign,
  });

  // Map TanStack's status/fetchStatus onto the four-value surface the
  // rest of the form (and `useExpenseConversion`) consume.
  const previewStatus: 'idle' | 'loading' | 'error' | 'success' =
    rateQuery.status === 'success'
      ? 'success'
      : rateQuery.status === 'error'
        ? 'error'
        : rateQuery.fetchStatus === 'fetching'
          ? 'loading'
          : 'idle';

  const conversion = useExpenseConversion({
    groupCurrency,
    enteredCurrency: enteredCurrencyState,
    enteredAmount,
    preview: rateQuery.data ?? null,
    manualRate,
    offline: !isOnline,
    frozen,
    storedAmounts: loadedConversion?.amounts,
  });

  const preview = {
    status: previewStatus,
    rate: rateQuery.data?.rate ?? null,
    rateDate: rateQuery.data?.rateDate ?? null,
    error: rateQuery.error ?? null,
    refetch: rateQuery.refetch,
  };

  // Group-currency amount for splits/payers — EXCEPT while queued
  // offline with no rate available, where both stay in the original
  // (entered) currency, matching the shape `createExpenseDto` queues.
  const numericAmount = conversion.numericAmount;
  const currency = conversion.pendingConversion
    ? enteredCurrencyState
    : groupCurrency;

  // Wrapper for setCategory that tracks manual changes
  const handleCategoryChange = useCallback((newCategory: string) => {
    dirtyRef.current = true;
    setCategory(newCategory);
    setCategoryManuallyChanged(true);
    setCategoryFromScan(false);
    // Clear subcategory when category changes manually
    setSubcategory(undefined);
    // Manual override invalidates any prediction metadata — the
    // sync-time upgrade treats `predictionMeta === null` as "user
    // picked, don't touch".
    setPredictionMeta(null);
  }, []);

  // Direct category setter that doesn't clear the subcategory (the
  // subcategory icon grid switches the top-level category through this).
  // It is a deliberate pick like `handleCategoryChange`: predictions stop
  // and the offline queue must not carry the rejected prediction's meta.
  const setCategoryDirect = useCallback((newCategory: string) => {
    dirtyRef.current = true;
    setCategory(newCategory);
    setCategoryManuallyChanged(true);
    setCategoryFromScan(false);
    setPredictionMeta(null);
  }, []);

  // Wrapper for setPaidBy that tracks manual changes
  const handlePaidByChange = useCallback(
    (newPaidBy: Payer[]) => {
      dirtyRef.current = true;
      setPaidBy(newPaidBy);
      // Only mark as manually changed if it's different from auto-managed state
      // (i.e., not a single payer with current user or amount different from total)
      const isAutoManaged =
        newPaidBy.length === 1 &&
        newPaidBy[0].userId === user?.id &&
        Math.abs(newPaidBy[0].amount - numericAmount) < 0.01;

      if (!isAutoManaged) {
        setPayersManuallyChanged(true);
      }
    },
    [user?.id, numericAmount]
  );

  // Both tiers are pure; the shared scheduler decides when each runs and
  // applies results only while the title is still current.
  const { predictCategory, predictCategoryCloud } = useCategoryPrediction({
    minConfidence: 0.4,
  });

  useEffect(() => {
    titleRef.current = title;
  });

  const onTitleBlur = useCallback(() => {
    setFlushNonce(n => n + 1);
  }, []);

  /**
   * Apply a prediction to the chip. `null` (title too short, or nothing
   * matched) and anything under the category floor clear the
   * auto-applied state: both tiers already filter at the floor, so a
   * sub-floor answer here is a contract violation, not a prediction.
   */
  const onPrediction = useCallback(
    (prediction: LivePrediction | CloudPredictionResult | null) => {
      setFallbackShown(false);
      if (!prediction || prediction.confidence < MIN_CATEGORY_CONFIDENCE) {
        setCategoryPredicted(false);
        setPredictionMeta(null);
        setDeferredFallback(null);
        return;
      }
      if (prediction.source === 'other-fallback') {
        // Invisible while the section is hidden; becomes the chip only if
        // the paid tier concludes without an answer (see `fallbackApplies`).
        setCategory('other');
        setSubcategory(undefined);
        setCategoryPredicted(false);
        setPredictionMeta(null);
        setDeferredFallback({
          title: titleRef.current.trim(),
          source: prediction.source,
          confidence: prediction.confidence,
        });
        return;
      }
      setDeferredFallback(null);
      setCategoryPredicted(true);
      setCategory(prediction.category);
      const sub = prediction.subcategory;
      setSubcategory(
        sub &&
          sub.confidence >= MIN_SUBCATEGORY_CONFIDENCE &&
          sub.type !== 'default'
          ? sub.type
          : undefined
      );
      // Remember which tier produced this category so the offline queue
      // can decide whether to upgrade on sync.
      setPredictionMeta({
        source: prediction.source,
        confidence: prediction.confidence,
      });
    },
    []
  );

  const { isLivePending, isCloudPending } = useCategoryPredictionScheduler<
    LivePrediction | CloudPredictionResult
  >({
    title,
    enabled: !categoryManuallyChanged && !scanInFlight,
    flushNonce,
    minTitleLength: MIN_TITLE_CHARS,
    runLive: predictCategory,
    runCloud: predictCategoryCloud,
    onPrediction,
  });
  // "Categorizing…" covers both tiers: the free tier's few ms, and the
  // paid tier's round trip while the placeholder is held back.
  const isPredicting = isLivePending || isCloudPending;
  // The held-back placeholder becomes the chip once the paid tier has
  // concluded without a better answer and the user hasn't picked one.
  // Keyed to its title: between a keystroke and the next free-tier run
  // there is no pending flag, and an unkeyed placeholder would flash as
  // the chip for the new title — the flip the deferral exists to prevent.
  const fallbackApplies =
    deferredFallback !== null &&
    deferredFallback.title === title.trim() &&
    !categoryManuallyChanged &&
    (!isCloudPending || fallbackShown);
  const effectivePredictionMeta =
    predictionMeta ??
    (fallbackApplies && deferredFallback
      ? {
          source: deferredFallback.source,
          confidence: deferredFallback.confidence,
        }
      : null);
  // Remember that the fallback has been shown (deferred a tick to keep
  // setState out of the effect body, like the other flags in this file).
  useEffect(() => {
    if (!fallbackApplies || fallbackShown) return;
    const timer = setTimeout(() => setFallbackShown(true), 0);
    return () => clearTimeout(timer);
  }, [fallbackApplies, fallbackShown]);

  /**
   * The form as a receipt scan sees it. A fill counts as touched, so once
   * its AI tag is gone (Remove) the value is the person's for the next scan.
   */
  const scanSnapshot = useCallback(
    (): Omit<ScanFormSnapshot, 'aiTagged'> => ({
      amount: enteredAmount > 0 ? enteredAmount : null,
      currency: enteredCurrencyState,
      title,
      date: date || null,
      category: category || null,
      subcategory: subcategory ?? null,
      touched: {
        ...touchedRef.current,
        categoryManual: categoryManuallyChanged,
      },
    }),
    [
      enteredAmount,
      enteredCurrencyState,
      title,
      date,
      category,
      subcategory,
      categoryManuallyChanged,
    ]
  );

  const applyScanFill = useCallback(
    (fills: ScanMerge['fills']) => {
      dirtyRef.current = true;
      const nextCurrency = fills.currency ?? enteredCurrencyState;
      if (fills.currency !== undefined) {
        const currencyFill = fills.currency;
        setEnteredCurrencyState(currencyFill);
        enteredCurrencyRef.current = currencyFill;
        setManualRateState(null);
        setItems(prev =>
          prev
            ? {
                ...prev,
                lineItems: roundLineItems(prev.lineItems, currencyFill),
              }
            : prev
        );
      }
      if (fills.amount !== undefined) {
        const rounded = roundToCurrencyPrecision(fills.amount, nextCurrency);
        touchedRef.current.amount = true;
        setEnteredAmount(rounded);
        setAmount(
          expenseFormService.formatAmountForCurrency(rounded, nextCurrency)
        );
      }
      if (fills.title !== undefined) {
        touchedRef.current.title = true;
        setTitle(fills.title);
      }
      if (fills.date !== undefined) {
        touchedRef.current.date = true;
        applyDate(fills.date);
      }
      if (fills.category !== undefined) {
        setCategory(fills.category);
        setSubcategory(fills.subcategory ?? undefined);
        setCategoryManuallyChanged(true);
        setCategoryFromScan(true);
        setPredictionMeta(null);
        setDeferredFallback(null);
      }
    },
    [enteredCurrencyState, applyDate]
  );

  // Update form state when data is loaded
  useEffect(() => {
    if (groupData) {
      // Wrap all state updates in setTimeout to avoid setting state during render
      const timer = setTimeout(() => {
        const group = groupData as ExtendedGroup;
        setGroup(group);

        if (!enteredCurrencyState && group.currency) {
          setEnteredCurrencyState(group.currency);
          enteredCurrencyRef.current = group.currency;
        }

        const groupChanged = seededGroupIdRef.current !== group.id;
        if (
          mode === 'create' &&
          !groupChanged &&
          keepSplitOnMemberChangeRef.current
        ) {
          const seeded = expenseFormService.initializeSplitsFromGroup(group);
          const inGroup = new Set(seeded.map(split => split.userId));
          // Once listed, a placed spot is an ordinary member: a later read
          // without them (removed elsewhere) drops the row like anyone's.
          for (const id of placedSpotIdsRef.current) {
            if (inGroup.has(id)) placedSpotIdsRef.current.delete(id);
          }
          const notListedYet = new Set(placedSpotIdsRef.current);
          setSplits(prev => {
            const kept = prev.filter(
              split =>
                inGroup.has(split.userId) || notListedYet.has(split.userId)
            );
            const known = new Set(kept.map(split => split.userId));
            const newcomers = seeded
              .filter(split => !known.has(split.userId))
              .map(split => ({ ...split, included: false }));
            return kept.length === prev.length && newcomers.length === 0
              ? prev
              : [...kept, ...newcomers];
          });
        } else if (
          mode === 'create' &&
          (groupChanged || !itemsRef.current?.itemsMode)
        ) {
          keepSplitOnMemberChangeRef.current = false;
          placedSpotIdsRef.current = new Set();
          setNewSpotIds(prev => (prev.length === 0 ? prev : []));
          if (groupChanged && seededGroupIdRef.current !== null) {
            // Picks name the old group's members.
            setItems(prev =>
              prev
                ? {
                    ...prev,
                    lineItems: prev.lineItems.map(item => ({
                      ...item,
                      sharedBy: [],
                      picks: [],
                    })),
                  }
                : prev
            );
          }
          seededGroupIdRef.current = group.id;

          // Use group's default split method if configured
          if (group.defaultSplitMethod) {
            setSplitMethod(group.defaultSplitMethod as SplitMethod);
          }

          // Initialize splits with default config (percentages/shares)
          // Pass the split method so 0% members can be auto-deselected
          const initialSplits = expenseFormService.initializeSplitsFromGroup(
            group,
            group.defaultSplitConfig,
            group.defaultSplitMethod
          );
          setSplits(initialSplits);
        }

        if (mode === 'create') {
          if (!paidBy.length) {
            setPaidBy(
              expenseFormService.initializeDefaultPayer(user?.id || '')
            );
            setPayersManuallyChanged(false); // Reset manual change flag for new expenses
          }
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [groupData, mode, user?.id, enteredCurrencyState, paidBy.length]);

  // Auto-update payer amount when total changes (with debouncing)
  useEffect(() => {
    // Auto-update paidBy amount when total changes
    if (numericAmount > 0) {
      // Check if we have exactly one payer (auto-managed state)
      const hasSinglePayer = paidBy.length === 1;

      // Auto-update if:
      // - We have a single payer AND
      // - Payers haven't been manually changed
      const shouldAutoUpdate = hasSinglePayer && !payersManuallyChanged;

      if (shouldAutoUpdate) {
        // Debounce the auto-update to prevent race conditions during typing
        const timer = setTimeout(() => {
          // Keep the same payer but update the amount
          setPaidBy([{ userId: paidBy[0].userId, amount: numericAmount }]);
        }, 100); // Slightly longer debounce than MultiPayerSelect to avoid conflicts

        return () => clearTimeout(timer);
      }
    }
  }, [numericAmount, paidBy, payersManuallyChanged]);

  // Load expense data for edit mode
  useEffect(() => {
    const fresherWhilePristine =
      hydratedIdRef.current === expenseData?.id &&
      !dirtyRef.current &&
      !scanInFlight &&
      !receiptBusy &&
      !!expenseData?.updatedAt &&
      !!expectedUpdatedAtRef.current &&
      // ISO timestamps: only a newer row, never an older cache restore.
      expenseData.updatedAt > expectedUpdatedAtRef.current;
    if (
      mode === 'edit' &&
      expenseData &&
      groupData &&
      (hydratedIdRef.current !== expenseData.id || fresherWhilePristine)
    ) {
      // Wrap all state updates in setTimeout to avoid setting state during render
      const timer = setTimeout(() => {
        // Set here, not before scheduling: a cleanup that cancels this timer
        // (a dep changing first) must not leave the form unhydrated.
        hydratedIdRef.current = expenseData.id;
        dirtyRef.current = false;
        const group = groupData as ExtendedGroup;
        setGroup(group);

        // Populate form fields from expense data
        if (expenseData) {
          const expenseCurrency =
            expenseData.currency || groupData.currency || 'USD';

          const parsedData = expenseFormService.parseExpenseForEdit(
            expenseData,
            expenseCurrency
          );
          setTitle(parsedData.title);
          // Raw setters, not the wrapped `setEnteredCurrency`/`setDate` —
          // those reset rate state this same effect is about to populate.
          setEnteredAmount(parsedData.enteredAmount);
          setEnteredCurrencyState(parsedData.enteredCurrency);
          enteredCurrencyRef.current = parsedData.enteredCurrency;
          setAmount(
            expenseFormService.formatAmountForCurrency(
              parsedData.enteredAmount,
              parsedData.enteredCurrency
            )
          );
          setManualRateState(parsedData.manualRate);
          setLoadedConversion({
            enteredCurrency: parsedData.enteredCurrency,
            date: parsedData.date,
            stored: parsedData.frozen,
            amounts: {
              amount: parsedData.numericAmount,
              originalAmount: parsedData.enteredAmount,
            },
          });
          // `ExpenseResponseDto` already declares these three fields —
          // no re-cast needed.
          setExistingConversion({
            originalCurrency: expenseData.originalCurrency,
            rateSource: expenseData.rateSource,
            exchangeRate: expenseData.exchangeRate,
          });
          setCategory(parsedData.category);
          setSubcategory(parsedData.subcategory);
          setCategoryManuallyChanged(true); // Mark as manually set to prevent ML from overwriting saved category
          setCategoryFromScan(false);
          setCategoryPredicted(true); // Mark as predicted so category and split UI are shown in edit mode
          setDateState(parsedData.date);
          setSplitMethod(parsedData.splitMethod as SplitMethod);
          setNotes(parsedData.notes);

          // Initialize receipt state from existing expense
          setExistingReceiptUrl(expenseData.receipt ?? null);
          // Every hydrated value is the person's for a scan's Use panel.
          touchedRef.current = { amount: true, title: true, date: true };
        }

        // Set paidBy from payers array
        const initializedPayers =
          expenseFormService.initializePayersFromExpense(
            expenseData,
            user?.id || ''
          );
        setPaidBy(initializedPayers);
        setPayersManuallyChanged(false); // Reset manual change flag when loading

        // Initialize splits from expense data
        const expenseSplits = expenseFormService.initializeSplitsFromExpense(
          group,
          expenseData
        );

        const hydratedItems = hydrateItemsState(expenseData);
        setItems(hydratedItems);
        loadedHadItemsRef.current = hydratedItems !== null;
        setStoredUnpicked(
          hydratedItems
            ? {
                split: expenseData.unpickedSplit,
                participants: hydratedItems.participants,
              }
            : null
        );
        // Sent with every save of a server row: the server checks it on the
        // itemized path, so a form hydrated before the expense was itemized
        // cannot drop the items unseen.
        expectedUpdatedAtRef.current = offlineExpenseService.isLocalId(
          expenseData.id
        )
          ? undefined
          : expenseData.updatedAt;
        if (hydratedItems) {
          // Zero shares have no split row; the participants are the stored list.
          const ids = new Set(hydratedItems.participants);
          setSplits(
            expenseSplits.map(split => ({
              ...split,
              included: ids.has(split.userId),
            }))
          );
        } else {
          setSplits(expenseSplits);
        }
        setHydratedExpenseId(expenseData.id);
        setHydrationKey(`${expenseData.id}:${expenseData.updatedAt ?? ''}`);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    expenseData,
    groupData,
    mode,
    user?.id,
    rehydrateNonce,
    scanInFlight,
    receiptBusy,
  ]);

  /** Loads the latest query data over the form, bypassing the once-per-id guard. */
  const rehydrate = useCallback(() => {
    if (mode !== 'edit') return;
    hydratedIdRef.current = null;
    setRehydrateNonce(n => n + 1);
  }, [mode]);

  // ===== Split by item =====
  const itemsAllowed = itemsAvailableIn(group, proFlagOn);
  const itemsMode = items?.itemsMode ?? false;

  const includedKey = splits
    .filter(split => split.included)
    .map(split => split.userId)
    .join(',');
  // Picks of someone no longer included are dropped before anything is sent.
  const effectiveItems = useMemo<ItemsFormState | null>(() => {
    if (!items) return null;
    const participants = includedKey ? includedKey.split(',') : [];
    const ids = new Set(participants);
    return {
      ...items,
      participants,
      lineItems: items.lineItems.map(item => {
        if (pickersOf(item).every(id => ids.has(id))) return item;
        if (!item.picks)
          return {
            ...item,
            sharedBy: item.sharedBy.filter(id => ids.has(id)),
          };
        const picks = item.picks.filter(p => ids.has(p.userId));
        return { ...item, picks, sharedBy: picks.map(p => p.userId) };
      }),
    };
  }, [items, includedKey]);
  const unpickedSplit = useMemo(() => {
    if (!effectiveItems) return undefined;
    return mode === 'edit' && storedUnpicked
      ? unpickedSplitForEdit(
          storedUnpicked.split,
          group,
          effectiveItems.participants,
          storedUnpicked.participants
        )
      : unpickedSplitFromGroup(group, effectiveItems.participants);
  }, [mode, storedUnpicked, group, effectiveItems]);

  // The server passes the entry only for a foreign expense; a queued one
  // with no rate is still all in the entered currency.
  const itemsEntry =
    conversion.isForeign && !conversion.pendingConversion
      ? { amount: enteredAmount, currency: enteredCurrencyState }
      : undefined;
  const entryAmount = itemsEntry?.amount;
  const entryCurrency = itemsEntry?.currency;
  const itemsPreview = useMemo(
    () =>
      effectiveItems && numericAmount > 0
        ? previewLineItemSplit(
            effectiveItems,
            numericAmount,
            currency,
            entryAmount !== undefined && entryCurrency
              ? { amount: entryAmount, currency: entryCurrency }
              : undefined,
            unpickedSplit
          )
        : null,
    [
      effectiveItems,
      numericAmount,
      currency,
      entryAmount,
      entryCurrency,
      unpickedSplit,
    ]
  );

  // ===== Repeat =====
  const repeatChoice = useRepeatChoice({
    availability: group
      ? {
          proEnabled: proFlagOn,
          user: user as { pro?: UserProDto } | null,
          group: {
            id: group.id,
            pro: (group as { pro?: GroupProDto }).pro,
            currency: groupCurrency,
          },
          isOnline,
          entryCurrency: isForeign ? enteredCurrencyState : groupCurrency,
          hasLineItems: itemsMode,
          mode,
        }
      : null,
    blockedByContent: isForeign || itemsMode,
    anchorDate: date,
    onChange: markDirty,
  });

  useEffect(() => {
    itemsRef.current = items;
  });
  const itemsAllowedRef = useRef(itemsAllowed);
  const groupDefaultMethod =
    (group?.defaultSplitMethod as SplitMethod | undefined) ?? 'equal';
  // A create that can no longer itemize (moved to the example group, or the
  // Pro flag went off) asks before the list goes, then falls back to the
  // group's default.
  useEffect(() => {
    const was = itemsAllowedRef.current;
    itemsAllowedRef.current = itemsAllowed;
    if (!was || itemsAllowed || !itemsRef.current?.itemsMode) return;
    if (itemsRef.current.lineItems.length > 0) {
      setPendingGroupSwitch(groupDefaultMethod);
    } else {
      setItems(null);
      setSplitMethod(groupDefaultMethod);
    }
  }, [itemsAllowed, groupDefaultMethod]);

  const setSplitMethodByUser = useCallback((method: SplitMethod) => {
    dirtyRef.current = true;
    splitTouchedRef.current = true;
    setSplitMethod(method);
  }, []);

  const setSplitsByUser = useCallback((next: Split[]) => {
    dirtyRef.current = true;
    splitTouchedRef.current = true;
    setSplits(next);
  }, []);

  const holdSplitOnMemberChange = useCallback(() => {
    keepSplitOnMemberChangeRef.current = true;
  }, []);

  /**
   * A spot saved from a picker joins the split as it stands; from who paid it
   * becomes the only payer and shares too, or "everyone" would quietly turn
   * into "everyone but the new payer".
   */
  const placeSavedSpot = useCallback(
    (member: SavedSpotMember, role: SavedSpotRole) => {
      dirtyRef.current = true;
      splitTouchedRef.current = true;
      keepSplitOnMemberChangeRef.current = true;
      placedSpotIdsRef.current.add(member.userId);
      setNewSpotIds(prev =>
        prev.includes(member.userId) ? prev : [...prev, member.userId]
      );
      // The cache write reaches `group` a tick later; the payer select needs
      // the option now.
      setGroup(prev =>
        prev && !(prev.members ?? []).some(m => m.userId._id === member.userId)
          ? {
              ...prev,
              members: [
                ...(prev.members ?? []),
                {
                  userId: { _id: member.userId, name: member.name },
                  isPlaceholder: true,
                },
              ],
            }
          : prev
      );
      setSplits(prev =>
        prev.some(split => split.userId === member.userId)
          ? prev.map(split =>
              split.userId === member.userId
                ? { ...split, included: true }
                : split
            )
          : [
              ...prev,
              {
                userId: member.userId,
                userName: member.name,
                included: true,
                isPlaceholder: true,
              },
            ]
      );
      // Under items mode the method is off screen; a later switch picks one anyway.
      if (!itemsRef.current?.itemsMode) {
        setSplitMethod(method =>
          method === 'percentage' || method === 'custom' ? 'equal' : method
        );
      }
      if (role === 'payer') {
        handlePaidByChange([
          {
            userId: member.userId,
            amount: roundToCurrencyPrecision(numericAmount, currency),
          },
        ]);
      }
    },
    [handlePaidByChange, numericAmount, currency]
  );

  const selectItemsMode = useCallback(() => {
    dirtyRef.current = true;
    splitTouchedRef.current = true;
    setItems(prev => ({ ...(prev ?? EMPTY_ITEMS), itemsMode: true }));
  }, []);

  /** Another method; leaving items mode drops the list (the UI has confirmed). */
  const chooseSplitMethod = useCallback((method: SplitMethod) => {
    dirtyRef.current = true;
    splitTouchedRef.current = true;
    setItems(prev => (prev?.itemsMode ? null : prev));
    setSplitMethod(method);
  }, []);

  const setLineItems = useCallback((lineItems: ExpenseLineItem[]) => {
    dirtyRef.current = true;
    setItems(prev => ({
      ...(prev ?? EMPTY_ITEMS),
      ...itemsListEdit(lineItems),
    }));
  }, []);

  /** Who had what; unlike a list edit it keeps the scan's reconcile flags. */
  const setLineItemPicks = useCallback((lineItems: ExpenseLineItem[]) => {
    dirtyRef.current = true;
    setItems(prev => ({ ...(prev ?? EMPTY_ITEMS), lineItems }));
  }, []);

  const setItemsRest = useCallback((rest: LineItemsRest) => {
    dirtyRef.current = true;
    setItems(prev => (prev ? { ...prev, rest } : prev));
  }, []);

  const toggleItemsParticipant = useCallback(
    (userId: string) => {
      const target = splits.find(split => split.userId === userId);
      if (!target) return;
      if (target.included && splits.filter(s => s.included).length === 1) {
        return;
      }
      dirtyRef.current = true;
      splitTouchedRef.current = true;
      setSplits(
        splits.map(split =>
          split.userId === userId
            ? { ...split, included: !split.included }
            : split
        )
      );
      // Unchecking someone drops their picks for good.
      if (target.included) {
        setItems(prev => {
          if (!prev) return prev;
          const lineItems = stripParticipantPicks(
            prev.lineItems,
            userId,
            splits.filter(split => split.included).map(split => split.userId)
          );
          return lineItems === prev.lineItems ? prev : { ...prev, lineItems };
        });
      }
    },
    [splits]
  );

  const applyScanLines = useCallback(
    (lines: ReceiptScanLineItemDto[], reconciled: boolean) => {
      const seeded = seedItemsFromScan(lines, randomUuid, reconciled, {
        currency: enteredCurrencyRef.current,
        unnamedLabel: tExpenses('lineItems.unnamed'),
      });
      const preselect =
        mode === 'create' &&
        reconciled &&
        !splitTouchedRef.current &&
        itemsAllowedRef.current;
      setItems(prev => ({
        ...(prev ?? EMPTY_ITEMS),
        ...seeded,
        itemsMode: (prev?.itemsMode ?? false) || preselect,
      }));
    },
    [mode, tExpenses]
  );

  /** The scan's `onLines`: a list with picks is only replaced after a confirm. */
  const onScanLines = useCallback(
    (lines: ReceiptScanLineItemDto[], reconciled: boolean) => {
      const hasPicks = !!itemsRef.current?.lineItems.some(
        item => pickersOf(item).length > 0
      );
      if (hasPicks) {
        setPendingScanLines({ lines, reconciled });
      } else {
        applyScanLines(lines, reconciled);
      }
    },
    [applyScanLines]
  );

  const confirmReplaceItems = useCallback(() => {
    if (pendingScanLines) {
      applyScanLines(pendingScanLines.lines, pendingScanLines.reconciled);
    }
    setPendingScanLines(null);
  }, [pendingScanLines, applyScanLines]);

  const keepItems = useCallback(() => setPendingScanLines(null), []);

  const confirmGroupSwitch = useCallback(() => {
    if (pendingGroupSwitch) {
      setItems(null);
      setSplitMethod(pendingGroupSwitch);
    }
    setPendingGroupSwitch(null);
  }, [pendingGroupSwitch]);

  const cancelGroupSwitch = useCallback(() => setPendingGroupSwitch(null), []);

  // A queued row whose items the server refused: its stored splits become
  // the custom amounts, then it saves once that state has rendered.
  const [customSaveNonce, setCustomSaveNonce] = useState(0);
  const customSaveRunningRef = useRef(false);
  const saveAsCustomSplit = useCallback(() => {
    if (customSaveRunningRef.current) return;
    customSaveRunningRef.current = true;
    splitTouchedRef.current = true;
    setItems(null);
    setSplitMethod('custom');
    setSplits(prev =>
      prev.map(split => ({ ...split, included: (split.amount ?? 0) > 0 }))
    );
    setCustomSaveNonce(n => n + 1);
  }, []);

  // Receipt and free-text setters the form calls from effects on mount too:
  // only a real value counts as an edit.
  const setNotesByUser = useCallback((value: string) => {
    dirtyRef.current = true;
    setNotes(value);
  }, []);
  const setSubcategoryByUser = useCallback((value: string | undefined) => {
    dirtyRef.current = true;
    setSubcategory(value);
  }, []);
  const setReceiptFileByUser = useCallback((file: File | null) => {
    if (file) dirtyRef.current = true;
    setReceiptFile(file);
  }, []);
  const setReceiptDeletedByUser = useCallback((deleted: boolean) => {
    if (deleted) dirtyRef.current = true;
    setReceiptDeleted(deleted);
  }, []);
  const setReceiptDraftIdByUser = useCallback((draftId: string | null) => {
    if (draftId) dirtyRef.current = true;
    setReceiptDraftId(draftId);
  }, []);

  // Calculate loading state
  const initialLoading =
    mode === 'edit' ? groupLoading || expenseLoading : groupLoading;

  const getSplitDisplayAmounts = useCallback(() => {
    return splitCalculationService.calculateSplitDisplayAmounts(
      numericAmount,
      currency,
      splitMethod,
      splits,
      {
        preferUserId: oddCentRecipient(
          paidBy.map(payer => payer.userId),
          splits.filter(split => split.included).map(split => split.userId)
        ),
      }
    );
  }, [numericAmount, currency, splits, splitMethod, paidBy]);

  // The server saves the expense without the new photo when the draft
  // expired or moved groups: no receipt, or on a Replace the old one still
  // in place. A queued row has not reached the server yet.
  const warnIfDraftDropped = (saved: { id?: string; receipt?: string }) => {
    if (!saved?.id || offlineExpenseService.isLocalId(saved.id)) return;
    if (
      receiptDraftDropped({
        sentDraftId: receiptDraftId,
        previousReceipt: existingReceiptUrl,
        savedReceipt: saved.receipt,
      })
    ) {
      toast.warning(tExpenses('scan.draftExpired'));
    }
  };

  // Form validation and submission
  const expenseLimit = group?.expenseLimit;
  const saveBlockedByLimit =
    mode === 'create' && expenseLimit?.remaining === 0 && !expenseLimit.exempt;

  const {
    handleSubmit: handleFormSubmit,
    isSubmitting: isLoading,
    error,
    setError,
  } = useFormWithValidation({
    onSubmit: async () => {
      // No `conversion.needsRate` gate here: `validateFn` below always
      // runs first and already returns `conversion.rateRequired` in that
      // case, so `onSubmit` never gets called at all — see `validateFn`.
      const conversionSnapshot: ConversionSnapshot = {
        isForeign: conversion.isForeign,
        enteredAmount,
        enteredCurrency: enteredCurrencyState,
        effectiveRate: conversion.effectiveRate,
        rateDate: conversion.rateDate,
        rateSource: conversion.rateSource,
        expenseDate: date,
      };

      // Items mode validates and sends the preview's splits; the `splits`
      // state may hold stale custom amounts from before the switch.
      const itemsSubmit =
        itemsMode && effectiveItems && itemsPreview
          ? { state: effectiveItems, preview: itemsPreview, unpickedSplit }
          : null;

      const formData: ExpenseFormData = {
        title: title.trim(), // Trim whitespace when saving
        numericAmount,
        currency,
        category,
        subcategory,
        date,
        notes,
        splitMethod: itemsSubmit
          ? 'custom'
          : (splitMethod as 'equal' | 'percentage' | 'custom'),
        paidBy,
        splits: itemsSubmit
          ? itemsSubmit.preview.splits.map(split => ({
              userId: split.userId,
              userName: '',
              amount: split.amount,
              included: true,
            }))
          : splits,
        groupCurrency,
        enteredAmount,
        enteredCurrency: enteredCurrencyState,
        pendingConversion: conversion.pendingConversion,
        conversionSnapshot,
      };

      // Validate form using service
      expenseFormService.validateExpenseForm(formData, t);

      const finalDisplayAmounts = getSplitDisplayAmounts();

      const itemsLineCurrency = itemsEntry?.currency ?? currency;
      const itemsPayload = itemsSubmit
        ? toItemsPayload(
            itemsSubmit.state,
            itemsSubmit.preview.splits,
            itemsLineCurrency,
            itemsSubmit.unpickedSplit
          )
        : mode === 'edit' && loadedHadItemsRef.current
          ? dropItemsPayload()
          : null;
      const afterSplitChange = (saved?: {
        id?: string;
        isOffline?: boolean;
      }) => {
        if (!itemsPayload) return;
        // A refetch would drop the queued row the list shows optimistically.
        if (saved?.isOffline) return;
        const savedId = saved?.id;
        for (const queryKey of SPLIT_CHANGE_KEYS(groupId)) {
          void queryClient.invalidateQueries({ queryKey });
        }
        if (savedId) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.expense(savedId),
          });
        }
      };

      // Use subcategory from state (already detected during ML prediction)

      if (mode === 'edit' && expenseId) {
        const updateData = {
          ...expenseFormService.createUpdateExpenseDto(
            formData,
            finalDisplayAmounts,
            existingConversion ?? undefined
          ),
          ...(itemsPayload ?? {}),
          ...(expectedUpdatedAtRef.current
            ? { expectedUpdatedAt: expectedUpdatedAtRef.current }
            : {}),
          // The draft wins over `receipt: null` on the server.
          ...(receiptDraftId
            ? {
                receiptDraftId,
                ...(receiptDeleted && existingReceiptUrl
                  ? { receipt: null }
                  : {}),
              }
            : {}),
        };
        const updatedExpense =
          await updateExpenseMutation.mutateAsync(updateData);
        afterSplitChange({
          ...updatedExpense,
          id: updatedExpense?.id ?? expenseId,
        });

        // Handle receipt upload/delete for existing expense. A queued
        // (offline-created) expense has no server id to attach a receipt
        // to yet, same as the create path.
        if (receiptDraftId) {
          warnIfDraftDropped(updatedExpense);
        } else if (
          receiptFile &&
          isOnline &&
          !offlineExpenseService.isLocalId(expenseId)
        ) {
          try {
            await uploadReceiptMutation.mutateAsync({
              expenseId,
              groupId,
              file: receiptFile,
            });
          } catch {
            // Non-blocking: expense saved, but receipt upload failed
            toast.warning(tExpenses('errors.receiptUploadFailed'));
          }
        } else if (receiptDeleted && existingReceiptUrl && !receiptFile) {
          // Receipt was deleted but no new file - remove from expense
          try {
            await deleteReceiptMutation.mutateAsync({ expenseId, groupId });
          } catch {
            // Non-blocking, same as the upload above: the expense is saved,
            // the receipt is still attached.
            toast.warning(tExpenses('errors.receiptRemoveFailed'));
          }
        }

        celebrate('subtle');
        toast.success(tExpenses('messages.expenseUpdated'));
        router.push(`/groups/${groupId}`);
      } else {
        const { repeat } = repeatChoice.payload();
        const createData = {
          ...expenseFormService.createExpenseDto(
            formData,
            groupId,
            finalDisplayAmounts
          ),
          ...(itemsSubmit
            ? toItemsPayload(
                itemsSubmit.state,
                itemsSubmit.preview.splits,
                itemsLineCurrency,
                itemsSubmit.unpickedSplit
              )
            : {}),
          ...(receiptDraftId ? { receiptDraftId } : {}),
          ...(repeat ? { repeat } : {}),
        };
        const createdExpense = await createExpenseMutation.mutateAsync({
          data: createData,
          // Only forward when an auto-prediction is the source of truth.
          // Manual picks leave `predictionMeta` as `null`, which tells
          // the sync hook to skip the cloud upgrade.
          predictionMeta:
            !categoryManuallyChanged &&
            !categoryFromScan &&
            effectivePredictionMeta
              ? effectivePredictionMeta
              : undefined,
        });
        afterSplitChange(createdExpense);

        // Upload receipt for newly created expense
        if (receiptDraftId) {
          warnIfDraftDropped(createdExpense);
        } else if (receiptFile && isOnline && createdExpense.id) {
          try {
            await uploadReceiptMutation.mutateAsync({
              expenseId: createdExpense.id,
              groupId,
              file: receiptFile,
            });
          } catch {
            // Non-blocking: expense saved, but receipt upload failed
            toast.warning(tExpenses('errors.receiptUploadFailed'));
          }
        }

        celebrate('medium');
        // A queued create carries `repeat` to the server on sync; nothing is
        // known about the rule yet, so it gets the plain toast.
        const queued =
          'isOffline' in createdExpense && !!createdExpense.isOffline;
        if (repeat && !queued) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.recurringRules(groupId),
          });
          if (createdExpense.recurringRuleId) {
            const sentence = cadenceSentence(repeat.cadence);
            toast.success(
              t('expenses:repeat.savedRepeating', {
                cadence: t(sentence.key, { count: sentence.count }),
              })
            );
          } else {
            toast.warning(t('expenses:repeat.notSet'));
          }
        } else {
          toast.success(tExpenses('messages.expenseCreated'));
        }
        router.push(`/groups/${groupId}`);
      }
    },
    onError: (err: unknown) => {
      if (
        mode === 'edit' &&
        expenseId &&
        (err as { translationKey?: unknown } | null)?.translationKey ===
          LINE_ITEMS_ERROR_KEYS.conflict
      ) {
        toast.error(t(LINE_ITEMS_ERROR_KEYS.conflict));
        void queryClient
          .refetchQueries(
            { queryKey: queryKeys.expense(expenseId) },
            { throwOnError: true }
          )
          // A failed refetch has nothing newer to show; the edits stay.
          .then(rehydrate, () => undefined);
        return;
      }
      if (
        mode === 'create' &&
        (err as { translationKey?: unknown } | null)?.translationKey ===
          GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached
      ) {
        // The API sends the count in `details`, which the shared form hook
        // does not read as translation params.
        const limit =
          (err as { details?: { limit?: number } }).details?.limit ??
          expenseLimit?.max;
        setError({
          key: GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached,
          params: { limit },
        });
        toast.error(t(GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached, { limit }));
        // The cached group still counted room left; the refetch shows the alert.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.group(groupId),
        });
        return;
      }
      const recurringKey = mode === 'create' ? recurringErrorKey(err) : null;
      if (recurringKey) {
        // Same as the limit above: the params (`limit`) arrive in `details`.
        const params = (err as { details?: Record<string, unknown> }).details;
        setError({ key: recurringKey, params });
        toast.error(t(recurringKey, params));
        return;
      }
      const errorMessage = handleError(err);
      toast.error(errorMessage);
    },
    validateFn: () => {
      if (!title.trim()) {
        return t('expenses:errors.titleRequired');
      }
      // Online, foreign, no rate resolved yet: `numericAmount` is 0 by
      // design (see `useExpenseConversion`), which would otherwise read
      // as "invalid amount". Surface the real reason instead.
      //
      // In edit mode a `pendingConversion` must also block submission of a
      // server expense: its edits are never queued for offline sync (unlike
      // create), so the update would post the entered amount labelled with
      // the group currency. `isOnline` here comes from a `/health` ping and
      // can be a false negative, so this cannot rely on being "offline". A
      // queued (local) expense is patched in the queue, where it waits for
      // conversion like a fresh one.
      if (
        conversion.needsRate ||
        (mode === 'edit' &&
          conversion.pendingConversion &&
          !offlineExpenseService.isLocalId(expenseId ?? ''))
      ) {
        return tExpenses('conversion.rateRequired');
      }
      if (!numericAmount || numericAmount <= 0) {
        return t('expenses:errors.invalidAmount');
      }
      // Moved to the example group and the person kept the list: its
      // sandbox cannot hold items.
      if (mode === 'create' && itemsMode && !itemsAllowed) {
        return t('errors:lineItems.notAllowed');
      }
      if (itemsMode && effectiveItems) {
        if (effectiveItems.participants.length === 0) {
          return t('expenses:errors.atLeastOnePersonRequired');
        }
        if (effectiveItems.lineItems.some(item => item.name.trim() === '')) {
          return tExpenses('lineItems.nameRequired');
        }
        if (!itemsPreview) return t('expenses:errors.invalidAmount');
      }
      return null;
    },
  });

  // While blocked, the limit alert hides the refusal; once the group turns Pro
  // the alert goes, and the refusal must not reappear beside an enabled Save.
  const wasBlockedByLimitRef = useRef(saveBlockedByLimit);
  useEffect(() => {
    const wasBlocked = wasBlockedByLimitRef.current;
    wasBlockedByLimitRef.current = saveBlockedByLimit;
    if (
      wasBlocked &&
      !saveBlockedByLimit &&
      typeof error !== 'string' &&
      error?.key === GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached
    ) {
      setError('');
    }
  }, [saveBlockedByLimit, error, setError]);

  const submitRef = useRef(handleFormSubmit);
  useEffect(() => {
    submitRef.current = handleFormSubmit;
  });
  useEffect(() => {
    if (customSaveNonce === 0) return;
    void submitRef
      .current({ preventDefault: () => undefined } as unknown as FormEvent, {})
      .finally(() => {
        customSaveRunningRef.current = false;
      });
  }, [customSaveNonce]);

  // Prepare members for MultiPayerSelect
  const members = orderPickerMembers(
    expenseFormService
      .prepareMembersForSelect(group)
      .map(m => ({ ...m, userName: m.name })),
    { currentUserId: user?.id ?? '', newIds: newSpotIds }
  );

  return {
    // Form data
    title,
    setTitle: setTitleByUser,
    amount,
    setAmount,
    numericAmount,
    setNumericAmount,
    currency,
    // Foreign-currency entry
    groupCurrency,
    enteredCurrency: enteredCurrencyState,
    setEnteredCurrency,
    enteredAmount,
    conversion,
    preview,
    manualRate,
    setManualRate,
    clearManualRate,
    isOfflineQueue: conversion.pendingConversion,
    category,
    setCategory: setCategoryDirect,
    handleCategoryChange,
    subcategory,
    setSubcategory: setSubcategoryByUser,
    setCategoryManuallyChanged,
    // A category is "showing" once a prediction applied or the user picked
    // one (edit mode hydrates with the manual flag set; a pick before any
    // prediction must not leave Save disabled) — but never for a title
    // under the classification floor, which also hides the category UI.
    // A scan category bypasses the floor: a receipt with no merchant leaves
    // the title empty and the category must stay.
    categoryPredicted:
      categoryFromScan ||
      ((categoryPredicted || categoryManuallyChanged || fallbackApplies) &&
        title.trim().length >= MIN_TITLE_CHARS),
    categoryFromScan,
    isPredicting,
    onTitleBlur,
    date,
    setDate,
    paidBy,
    setPaidBy: handlePaidByChange,
    splitMethod,
    setSplitMethod: setSplitMethodByUser,
    splits,
    setSplits: setSplitsByUser,
    holdSplitOnMemberChange,
    placeSavedSpot,
    newSpotIds,
    notes,
    setNotes: setNotesByUser,

    // Split by item
    items: effectiveItems,
    itemsMode,
    itemsAllowed,
    itemsPreview,
    /** Item amounts and the dialog's total are in the entry currency. */
    itemsAmount: itemsEntry?.amount ?? numericAmount,
    itemsCurrency: itemsEntry?.currency ?? currency,
    setAmountFromItems,
    selectItemsMode,
    chooseSplitMethod,
    setLineItems,
    setLineItemPicks,
    setItemsRest,
    toggleItemsParticipant,
    onScanLines,
    pendingItemsReplace: pendingScanLines !== null,
    confirmReplaceItems,
    keepItems,
    pendingGroupSwitch,
    confirmGroupSwitch,
    cancelGroupSwitch,
    saveAsCustomSplit,

    // Repeat
    repeatAvailable: repeatChoice.available,
    repeatCadence: repeatChoice.cadence,
    setRepeatCadence: repeatChoice.setCadence,
    repeatEndDate: repeatChoice.endDate,
    setRepeatEndDate: repeatChoice.setEndDate,
    repeatPreview: repeatChoice.preview,
    repeatPlainOnly: repeatChoice.plainOnly,

    // Receipt state
    receiptFile,
    setReceiptFile: setReceiptFileByUser,
    existingReceiptUrl,
    receiptDeleted,
    setReceiptDeleted: setReceiptDeletedByUser,
    receiptDraftId,
    setReceiptDraftId: setReceiptDraftIdByUser,
    isOnline,

    // Receipt scan
    scanSnapshot,
    applyScanFill,
    setScanInFlight,
    setReceiptBusy,
    hydratedExpenseId,
    hydrationKey,
    rehydrate,

    // Computed values
    group,
    members,
    getSplitDisplayAmounts,
    expenseLimit,
    saveBlockedByLimit,

    // Loading states
    initialLoading,
    isLoading,
    isUploadingReceipt: uploadReceiptMutation.isPending,

    // Form submission
    handleFormSubmit,
    error,

    // Utils
    user,
    mode,
  };
}
